/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { ContentEditableEvent, getUrlSync, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IEveBusinessSignUpConfig, IEveBusinessSignUpProps, IEveBusinessSignUpResources } from './eve-business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components';

export interface IBusinessSignUpExtendedProps extends IEveBusinessSignUpProps<IEveBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    fieldReference?: React.RefObject<HTMLInputElement>;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'CompanyEmail',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    JobTitle = 'JobTitle',
    Size = 'CompanySize',
    Department = 'Department',
    Comment = 'Comment'
}

const renderFieldFeedbackMessage = (
    fieldName: string,
    isRequired: boolean,
    fieldPattern: string | undefined,
    showErrors: boolean,
    value: string,
    invalidFieldMessage: string,
    missingFieldMessage: string
): string | undefined => {
    if (!showErrors) {
        return undefined;
    } else if (!value && isRequired) {
        return missingFieldMessage.replace('{0}', fieldName);
    } else if (value && fieldPattern && !new RegExp(fieldPattern).test(value)) {
        return invalidFieldMessage.replace('{0}', fieldName);
    }
    return undefined;
};

export const renderInputSection = (
    resources: IEveBusinessSignUpResources,
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    showErrors: boolean,
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void
): IBusinessSignUpItem[] => {
    const { fieldRequiredError, fieldInvalidError } = resources;
    return inputs.map((element, index) => {
        const { id, type, label, maxChars, isRequired, pattern, customClass, value, fieldReference } = element;
        const fieldName = label?.replace('*', '').trim() ?? label;
        const message = renderFieldFeedbackMessage(
            fieldName,
            isRequired,
            pattern,
            showErrors,
            value,
            fieldInvalidError,
            fieldRequiredError
        );
        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: classnames(className, `${className}-${id}`, customClass)
            },
            id,
            label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: <SignUpError {...{ className, type: 'item', message }} />
        };

        if (type === InputType.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress,
                            hasError: showErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else {
            baseSignUpItem.input = (
                <SignUpInput
                    {...{
                        id,
                        type,
                        value,
                        maxLength: maxChars,
                        pattern,
                        className,
                        isRequired: isRequired === undefined ? false : isRequired,
                        onChange: onInputChange,
                        fieldReference
                    }}
                />
            );
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<IEveBusinessSignUpProps<IEveBusinessSignUpConfig>>}
 */
class BusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    // Used as default data
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25'
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25'
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50'
        },
        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.Department,
            type: InputType.Text,
            label: this.props.resources.departmentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.JobTitle,
            type: InputType.Text,
            label: this.props.resources.jobTitleLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.Size,
            type: InputType.Text,
            label: this.props.resources.companySizeLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            label: this.props.resources.companyAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.Comment,
            type: InputType.TextArea,
            label: this.props.resources.commentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly moduleClassName: string = 'ms-business-sign-up';

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isReady: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        this.formChildReference.current?.focus();
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;

        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Text
                    className={`${this.moduleClassName}__heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const { showAddressErrors, hasSubmitErrors, formItems } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage
        } = resources;
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;
        const successPageURL =
            (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
            getUrlSync('home', this.props.context.actionContext);

        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection(
                resources,
                formItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange,
                showAddressErrors,
                addressForm,
                this._onAddressChange
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    link={successPageURL}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={false}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                )
            ],
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            )
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private readonly _onInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const newForm = this.state.formItems;
        newForm.forEach(item => {
            if (item.id === event.target.id) {
                item.value = event.target.value;
            }
        });

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    };

    private _addedReference(): IFormItem[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address): void => {
        // Find address item
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currentAddress;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(),
                formItems: newForm,
                addressHasErrors: errors,
                showAddressErrors: false
            });
        }
    };

    private readonly _isFormReady = (): boolean => {
        const { formItems } = this.state;

        for (const currentInput of formItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern

            if (currentInput.isRequired) {
                if (currentInput.type === InputType.Address) {
                    if (this.state.addressHasErrors) {
                        return false;
                    }
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
            }
            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }

        return true;
    };

    private readonly _onSubmit = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (this.state.addressHasErrors) {
            this.setState({
                showAddressErrors: true
            });

            event.preventDefault();
            return;
        }

        if (!this.state.isReady) {
            return;
        }

        const { context } = this.props;

        // Check for matching id and if not empty
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const companyName = this._getItem(InputID.CompanyName);
        const companyEmail = this._getItem(InputID.Email);
        const companyAddress = this._getItem(InputID.Address, true);
        const jobTitle = this._getItem(InputID.JobTitle);
        const department = this._getItem(InputID.Department);
        const companySize = this._getItem(InputID.Size);
        const comments = this._getItem(InputID.Comment);

        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (companyEmail && companyEmail.value && companyAddress && companyAddress.address) {
            companyAddress.address.Email = companyEmail.value;
        }

        const newPartner: BusinessPartnerProspect = {
            FirstName: firstName && firstName.value,
            LastName: lastName && lastName.value,
            CompanyName: companyName && companyName.value,
            Address: companyAddress && companyAddress.address && companyAddress.address,
            JobTitle: jobTitle && jobTitle.value,
            Department: department && department.value,
            CompanySize: (companySize && Number.parseInt(companySize.value, 10)) || 0,
            Comments: comments && comments.value
        };

        // Create business partner request
        createProspectAsync({ callerContext: context.actionContext }, newPartner).catch(() => {
            this.setState({
                hasSubmitErrors: true,
                isReady: false
            });
        });
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.formItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            return item.id === id && item.value !== '';
        });
    };
}

export default BusinessSignUp;
